import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IoClose, IoShareOutline, IoGlobeOutline, IoPeopleOutline, IoLockClosedOutline, IoChevronDown, IoHappyOutline, IoPersonAddOutline } from 'react-icons/io5';
import Avatar from './Avatar';
import VoicePlayer from './VoicePlayer';
import { useToast } from './Toast';
import { useAuth } from '../hooks/useAuth';
import { postApi } from '../services/postApi';
import { formatDistanceToNow } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import './SharePostModal.css';

// Emoji categories with icons and emojis
const emojiCategories = {
  recently: { icon: '🕐', label: 'Recently', emojis: [] },
  smileys: { icon: '😀', label: 'Smileys', emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕', '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱'] },
  animals: { icon: '🐶', label: 'Animals', emojis: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🐢', '🐍', '🦎', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛', '🦏'] },
  food: { icon: '🍔', label: 'Food', emojis: ['🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🌭', '🍔', '🍟', '🍕', '🫓', '🥪', '🥙', '🧆', '🌮', '🌯', '🫔', '🥗', '🥘', '🫕', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱'] },
  activities: { icon: '⚽', label: 'Activities', emojis: ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛼', '🛷', '⛸️', '🥌', '🎿', '⛷️', '🏂', '🪂', '🏋️', '🤼', '🤸', '🤺', '⛹️', '🤾', '🏌️', '🏇', '🧘', '🏄', '🏊', '🤽', '🚣', '🧗', '🚴', '🚵', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺', '🎸', '🪕', '🎻'] },
  travel: { icon: '🚗', label: 'Travel', emojis: ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🛻', '🚚', '🚛', '🚜', '🏍️', '🛵', '🚲', '🛴', '🛺', '🚨', '🚔', '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇', '🚊', '🚉', '✈️', '🛫', '🛬', '🛩️', '💺', '🛰️', '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛥️', '🛳️', '⛴️', '🚢', '⚓', '🪝', '⛽', '🚧', '🚦', '🚥'] },
  objects: { icon: '💡', label: 'Objects', emojis: ['⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🪜', '🧰', '🪛', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🪚', '🔩', '⚙️', '🪤', '🧱', '⛓️', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🕳️', '🩹', '🩺', '💊', '💉', '🩸', '🧬', '🦠', '🧫', '🧭'] },
  symbols: { icon: '❤️', label: 'Symbols', emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳', '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️', '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️', '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓', '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️', '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠', 'Ⓜ️', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🛗', '🈳', '🈂️', '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼', '⚧', '🚻', '🚮', '🎦', '📶', '🈁', '🔣', 'ℹ️', '🔤', '🔡', '🔠', '🆖', '🆗', '🆙', '🆒', '🆕', '🆓', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '🔢', '#️⃣', '*️⃣', '⏏️', '▶️', '⏸️', '⏯️', '⏹️', '⏺️', '⏭️', '⏮️', '⏩', '⏪', '⏫', '⏬', '◀️', '🔼', '🔽', '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '↪️', '↩️', '⤴️', '⤵️', '🔀', '🔁', '🔂', '🔄', '🔃', '🎵', '🎶', '➕', '➖', '➗', '✖️', '🟰', '♾️', '💲', '💱', '™️', '©️', '®️', '〰️', '➰', '🔚', '🔙', '🔛', '🔝', '🔜', '✔️', '☑️', '🔘', '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔺', '🔻', '🔸', '🔹', '🔶', '🔷', '🔳', '🔲', '▪️', '▫️', '◾', '◽', '◼️', '◻️', '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '⬛', '⬜', '🟫', '🔈', '🔇', '🔉', '🔊', '🔔', '🔕', '📣', '📢', '💬', '💭', '🗯️', '♠️', '♣️', '♥️', '♦️', '🃏', '🎴', '🀄', '🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛', '🕜', '🕝', '🕞', '🕟', '🕠', '🕡', '🕢', '🕣', '🕤', '🕥', '🕦', '🕧'] },
  flags: { icon: '🏳️', label: 'Flags', emojis: ['🏳️', '🏴', '🏴‍☠️', '🏁', '🚩', '🎌', '🏳️‍🌈', '🏳️‍⚧️', '🏴󠁧󠁢󠁥󠁮󠁧󠁿', '🏴󠁧󠁢󠁳󠁣󠁴󠁿', '🏴󠁧󠁢󠁷󠁬󠁳󠁿', '🇺🇸', '🇬🇧', '🇫🇷', '🇩🇪', '🇮🇹', '🇪🇸', '🇵🇹', '🇻🇳', '🇨🇦', '🇦🇺', '🇯🇵', '🇰🇷', '🇨🇳', '🇮🇳', '🇧🇷', '🇲🇽', '🇷🇺', '🇰🇵', '🇵🇸', '🇪🇨', '🇮🇪', '🇳🇱', '🇧🇪', '🇨🇭', '🇦🇹', '🇵🇱', '🇱🇹', '🇱🇻', '🇪🇪', '🇱🇺', '🇭🇷', '🇷🇸', '🇸🇮', '🇧🇦', '🇲🇪', '🇲🇰', '🇬🇷', '🇹🇷', '🇺🇦', '🇧🇾', '🇦🇲', '🇦🇿', '🇬🇪', '🇰🇿', '🇰🇬', '🇹🇯', '🇹🇿', '🇰🇪', '🇰🇷', '🇲🇦', '🇩🇿', '🇱🇾', '🇹🇳', '🇪🇬', '🇸🇦', '🇦🇪', '🇴🇲', '🇶🇦', '🇧🇭', '🇰🇼', '🇴🇲', '🇾🇪', '🇸🇾', '🇮🇶', '🇱🇧', '🇸🇩', '🇸🇸', '🇪🇷', '🇪🇹', '🇺🇬', '🇰🇪', '🇹🇿', '🇲🇿', '🇿🇦', '🇳🇦', '🇧🇼', '🇳🇦', '🇿🇲', '🇿🇼', '🇲🇼', '🇲🇬', '🇨🇲', '🇨🇩', '🇨🇫', '🇨🇬', '🇨🇮', '🇸🇳', '🇬🇭', '🇬🇳', '🇬🇲', '🇬🇶', '🇬🇼', '🇸🇱', '🇸🇬', '🇲🇾', '🇮🇩', '🇵🇭', '🇹🇭', '🇲🇲', '🇰🇭', '🇱🇦', '🇳🇵', '🇧🇹', '🇦🇫', '🇱🇰', '🇲🇻', '🇲🇪', '🇮🇴', '🇫🇴', '🇸🇭', '🇲🇫', '🇹🇦', '🇬🇸', '🇫🇯', '🇵🇫', '🇼🇸', '🇬🇺', '🇲🇵', '🇵🇼', '🇲🇬', '🇵🇬', '🇸🇧', '🇻🇺', '🇫🇲', '🇲🇭', '🇳🇷', '🇳🇫', '🇵🇳', '🇹🇰', '🇲🇶', '🇧🇱', '🇲🇸', '🇬🇩', '🇭🇹', '🇩🇲', '🇨🇵', '🇬🇵', '🇲🇶', '🇲🇳', '🇦🇬', '🇧🇧', '🇨🇼', '🇨🇩', '🇨🇺', '🇩🇴', '🇭🇳', '🇯🇲', '🇵🇾', '🇸🇲', '🇸🇻', '🇬🇹', '🇬🇾', '🇧🇿', '🇧🇲', '🇦🇮', '🇰🇳', '🇱🇨', '🇲🇫', '🇲🇸', '🇵🇲', '🇦🇼', '🇨🇽', '🇳🇨', '🇵🇫', '🇼🇫'] },
};

// Flatten all emojis for search
const allEmojis = Object.values(emojiCategories).flatMap(cat => cat.emojis);

// Get recently used emojis from localStorage
const getRecentlyUsedEmojis = () => {
  try {
    const stored = localStorage.getItem('recentEmojis');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save emoji to recently used
const saveRecentlyUsedEmoji = (emoji) => {
  try {
    let recent = getRecentlyUsedEmojis();
    recent = [emoji, ...recent.filter(e => e !== emoji)].slice(0, 16);
    localStorage.setItem('recentEmojis', JSON.stringify(recent));
  } catch {
    // Ignore storage errors
  }
};

const SharePostModal = ({ post, onClose, onShared }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const showToast = useToast();
  const [commentary, setCommentary] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [visibility, setVisibility] = useState(() => localStorage.getItem('postVisibility') || 'public');
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('smileys');
  const [recentEmojis, setRecentEmojis] = useState(getRecentlyUsedEmojis);
  const [emojiPickerPosition, setEmojiPickerPosition] = useState({ top: 0, left: 0 });
  const visibilityRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const textareaRef = useRef(null);
  const locale = i18n.language === 'vi' ? vi : enUS;

  const visibilityOptions = [
    { value: 'public', icon: IoGlobeOutline, label: t('post.visibilityPublic') || 'Public' },
    { value: 'friends', icon: IoPeopleOutline, label: t('post.visibilityFriends') || 'Friends' },
    { value: 'only_me', icon: IoLockClosedOutline, label: t('post.visibilityOnlyMe') || 'Only me' },
  ];

  const currentVisibility = visibilityOptions.find(o => o.value === visibility) || visibilityOptions[1];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (visibilityRef.current && !visibilityRef.current.contains(e.target)) {
        setShowVisibilityMenu(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowEmojiPicker(false);
        setEmojiSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Calculate emoji picker position when it opens
  useEffect(() => {
    if (showEmojiPicker && emojiButtonRef.current) {
      const button = emojiButtonRef.current;
      const rect = button.getBoundingClientRect();
      const pickerHeight = 360;
      const offset = 8;
      // Position above the button
      setEmojiPickerPosition({
        top: rect.top - pickerHeight - offset,
        left: rect.left,
      });
    }
  }, [showEmojiPicker]);

  // Insert emoji at cursor position
  const insertEmoji = (emoji) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newText = commentary.slice(0, start) + emoji + commentary.slice(end);
      setCommentary(newText);
      // Set cursor position after emoji
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setCommentary(commentary + emoji);
    }
    // Save to recently used
    saveRecentlyUsedEmoji(emoji);
    setRecentEmojis(getRecentlyUsedEmojis());
    // Don't close the picker - user can add multiple emojis
    setEmojiSearch('');
  };

  // Close emoji picker when clicking outside
  const handleEmojiPickerClose = () => {
    setShowEmojiPicker(false);
    setEmojiSearch('');
  };

  // Handle click outside for emoji picker
  useEffect(() => {
    if (!showEmojiPicker) return;

    const handleClickOutside = (e) => {
      // Check if click is outside both the button and the picker
      const isOutsideButton = emojiButtonRef.current && !emojiButtonRef.current.contains(e.target);
      const isOutsidePicker = emojiPickerRef.current && !emojiPickerRef.current.contains(e.target);

      if (isOutsideButton && isOutsidePicker) {
        setShowEmojiPicker(false);
        setEmojiSearch('');
      }
    };

    // Add a small delay to avoid immediate trigger when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Insert @ for tagging
  const handleTagPeople = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const newText = commentary.slice(0, start) + '@' + commentary.slice(start);
      setCommentary(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 1, start + 1);
      }, 0);
    } else {
      setCommentary(commentary + '@');
    }
  };

  // Auto-resize textarea on mount
  useEffect(() => {
    if (textareaRef.current && commentary) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, []);

  // Auto-expand textarea
  const handleTextareaChange = (e) => {
    setCommentary(e.target.value);
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  };

  // Ctrl+Enter to share
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (commentary.trim() && !isSharing) {
        handleShare();
      }
    }
  };

  const handleVisibilityChange = (value) => {
    setVisibility(value);
    localStorage.setItem('postVisibility', value);
    setShowVisibilityMenu(false);
  };

  // Get the original post to share (if this is already a shared post, share the original)
  const originalPost = post.shared_post || post;

  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale });
    } catch {
      return timestamp;
    }
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      await postApi.sharePost(originalPost.id, commentary, visibility);
      onClose();
      if (onShared) onShared();
      showToast(t('post.shareSuccess') || 'Post shared successfully!');
    } catch (error) {
      console.error('Error sharing post:', error);
      showToast(t('post.shareError') || 'Failed to share post', 'error');
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h3>
            <IoShareOutline />
            {t('post.sharePost') || 'Share this post'}
          </h3>
          <button className="share-modal-close" onClick={onClose}>
            <IoClose />
          </button>
        </div>

        <div className="share-modal-body">
          <div className="share-composer">
            <Avatar user={user} size={40} />
            <div className="share-composer-input">
              <div className="share-composer-name">{user?.display_name}</div>
              <textarea
                ref={textareaRef}
                className="share-commentary"
                placeholder={t('post.addCommentary') || 'Say something about this...'}
                value={commentary}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                rows="3"
                maxLength="5000"
                autoFocus
              />

              {/* Quick Actions Row */}
              <div className="share-quick-actions">
                <div className="quick-action-emoji">
                  <button
                    ref={emojiButtonRef}
                    type="button"
                    className="quick-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEmojiPicker(!showEmojiPicker);
                    }}
                    title={t('common.emoji') || 'Emoji'}
                  >
                    <IoHappyOutline />
                  </button>
                </div>
                <button
                  type="button"
                  className="quick-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTagPeople();
                  }}
                  title={t('post.tagPeople') || 'Tag people'}
                >
                  <IoPersonAddOutline />
                </button>
              </div>
            </div>
          </div>

          <div className="share-preview">
            <div className="share-preview-user-info">
              <Avatar user={originalPost.user} size={36} />
              <div className="share-preview-meta">
                <span className="share-preview-name">{originalPost.user?.display_name}</span>
                <span className="share-preview-dot">·</span>
                <span className="share-preview-time">{formatTime(originalPost.created_at)}</span>
              </div>
            </div>
            {originalPost.content && (
              <p className="share-preview-text">{originalPost.content}</p>
            )}

            {originalPost.voice_url && originalPost.voice_duration && (
              <div className="share-preview-voice">
                <VoicePlayer voiceUrl={originalPost.voice_url} duration={originalPost.voice_duration} compact={true} />
              </div>
            )}

            {originalPost.media_urls && originalPost.media_urls.length > 0 && (
              <div className="share-preview-media">
                {originalPost.media_urls.slice(0, 2).map((url, index) => (
                  originalPost.media_types[index] === 'video' ? (
                    <div key={index} className="share-preview-media-item">
                      <video src={url} preload="metadata" />
                    </div>
                  ) : (
                    <img key={index} src={url} alt={`Media ${index + 1}`} className="share-preview-media-item" />
                  )
                ))}
                {originalPost.media_urls.length > 2 && (
                  <span className="share-preview-more">+{originalPost.media_urls.length - 2}</span>
                )}
              </div>
            )}

            {originalPost.link_preview && (
              <div className="share-preview-link">
                {originalPost.link_preview.title || originalPost.link_preview.url}
              </div>
            )}
          </div>
        </div>

        <div className="share-modal-footer">
          <div className="visibility-selector" ref={visibilityRef}>
            <button
              type="button"
              className="visibility-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setShowVisibilityMenu(!showVisibilityMenu);
              }}
            >
              <currentVisibility.icon />
              <span>{currentVisibility.label}</span>
              <IoChevronDown className="visibility-chevron" />
            </button>
            {showVisibilityMenu && (
              <div className="visibility-menu">
                {visibilityOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={`visibility-option ${option.value === visibility ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVisibilityChange(option.value);
                    }}
                  >
                    <option.icon />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="share-modal-buttons">
            <button className="btn btn-text" onClick={onClose} disabled={isSharing}>
              {t('common.cancel') || 'Cancel'}
            </button>
            <button className="btn btn-primary" onClick={handleShare} disabled={isSharing}>
              {isSharing ? '...' : (t('post.share') || 'Share')}
            </button>
          </div>
        </div>
      </div>

      {/* Emoji Picker Portal - rendered at document body level */}
      {showEmojiPicker && ReactDOM.createPortal(
        <div className="emoji-picker-wrapper" ref={emojiPickerRef} onClick={(e) => e.stopPropagation()}>
          <div
            className="emoji-picker-portal"
            style={{
              top: emojiPickerPosition.top,
              left: emojiPickerPosition.left,
            }}
          >
            <div className="emoji-picker">
              {/* Search */}
              <div className="emoji-search">
                <input
                  type="text"
                  placeholder={t('common.searchEmoji') || 'Search emoji...'}
                  value={emojiSearch}
                  onChange={(e) => setEmojiSearch(e.target.value)}
                  autoFocus
                />
              </div>

              {/* Category Tabs */}
              <div className="emoji-categories">
                {Object.entries(emojiCategories).map(([key, cat]) => (
                  <button
                    key={key}
                    type="button"
                    className={`emoji-category-btn ${activeCategory === key ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCategory(key);
                      setEmojiSearch('');
                    }}
                    title={cat.label}
                  >
                    {cat.icon}
                  </button>
                ))}
              </div>

              {/* Emoji Grid */}
              <div className="emoji-grid">
                {emojiSearch ? (
                  // Search results
                  allEmojis
                    .filter(e => e.includes(emojiSearch) || e.includes(emojiSearch))
                  .slice(0, 48)
                  .map((emoji, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="emoji-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        insertEmoji(emoji);
                      }}
                    >
                      {emoji}
                    </button>
                  ))
              ) : activeCategory === 'recently' && recentEmojis.length > 0 ? (
                // Recently used
                recentEmojis.map((emoji, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="emoji-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        insertEmoji(emoji);
                      }}
                  >
                    {emoji}
                  </button>
                ))
              ) : (
                // Category emojis
                (emojiCategories[activeCategory]?.emojis || []).map((emoji, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="emoji-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        insertEmoji(emoji);
                      }}
                  >
                    {emoji}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SharePostModal;
